import { useCallback } from 'react';
import { type FileWithPath } from 'react-dropzone';

import { useTrackDepletedActionBlocked } from '@air/analytics';
import { Clips } from '@air/api';
import { NO_MORE_CREDITS, NO_MORE_STORAGE, SUBSCRIPTION_EXPIRED } from '@air/errors';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { CreditsDepletedModal } from '~/components/Modals/CreditsDepletedModal';
import { MissingFileExtensionsModal } from '~/components/Modals/MissingFileExtenionsModal';
import { OutOfStorageModal } from '~/components/Modals/OutOfStorageModal';
import { RestrictedCharacterReplacementModal } from '~/components/Modals/RestrictedCharacterReplacementModal';
import { SubscriptionExpiredModal } from '~/components/Modals/SubscriptionExpiredModal';
import { getSortedFilesAndMetadata } from '~/components/PrivateDropzone/utilities/dropzones';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { sanitizeRestrictedPathCharsInFile } from '~/utils/FileUtils';

export type ValidatedFiles = {
  files: FileWithPath[];
  emptyFiles: FileWithPath[];
  filesWithNoExtension: FileWithPath[];
  totalSize: number;
};

export type UseUploadValidationOptions = {
  workspaceId?: string;
};

export type UseUploadValidationReturn = {
  validateUpload: (acceptedFiles: FileWithPath[]) => Promise<ValidatedFiles | null>;
};

/**
 * Hook to validate files before upload:
 * - Check storage limits and permissions
 * - Identify and handle files without extensions
 * - Identify and handle files with restricted characters
 * - Sanitize file names where needed
 *
 * @returns Object with validateUpload function that returns validated files or null if upload should be cancelled
 */
export const useUploadValidation = ({ workspaceId }: UseUploadValidationOptions): UseUploadValidationReturn => {
  const { showToast } = useToasts();
  const [showOutOfStorageModal] = useAirModal(OutOfStorageModal);
  const [showCreditsDepletedModal] = useAirModal(CreditsDepletedModal);
  const [showSubscriptionExpiredModal] = useAirModal(SubscriptionExpiredModal);
  const [showMissingFileExtensionsModal] = useAirModal(MissingFileExtensionsModal);
  const [showRestrictedCharacterConfirmationModal] = useAirModal(RestrictedCharacterReplacementModal);
  const { trackDepletedActionBlocked } = useTrackDepletedActionBlocked();
  const creditAnalyticsParams = useCreditAnalyticsParams();

  const checkIfCanUpload = useCallback(
    async ({ files, totalSize }: { totalSize: number; files: FileWithPath[] }) => {
      if (!workspaceId) {
        return false;
      }

      try {
        const { canUpload, failureReason } = await Clips.checkCanUpload({ workspaceId, size: totalSize });

        if (!canUpload) {
          switch (failureReason) {
            case NO_MORE_STORAGE.message:
              showOutOfStorageModal();
              break;
            case NO_MORE_CREDITS.message:
              if (creditAnalyticsParams) {
                trackDepletedActionBlocked({
                  action: 'upload',
                  days_since_cycle_start: creditAnalyticsParams.days_since_cycle_start,
                });
              }
              showCreditsDepletedModal();
              break;
            case SUBSCRIPTION_EXPIRED.message:
              showSubscriptionExpiredModal();
              break;
            default:
              throw new Error(`Expected failureReason ${NO_MORE_STORAGE.message}, Received: ${failureReason}`);
          }
        }

        return canUpload;
      } catch (error: unknown) {
        showToast('There was an error while trying to upload items, please try again later');
        reportErrorToBugsnag({
          error,
          context: 'useUploadValidation: Failed to check if can upload',
          metadata: { data: { files: files.map((f) => ({ name: f.name, size: f.size })) } },
        });
        return false;
      }
    },
    [
      creditAnalyticsParams,
      showCreditsDepletedModal,
      showOutOfStorageModal,
      showSubscriptionExpiredModal,
      showToast,
      trackDepletedActionBlocked,
      workspaceId,
    ],
  );

  const validateUpload = useCallback(
    async (acceptedFiles: FileWithPath[]): Promise<ValidatedFiles | null> => {
      const { emptyFiles, filesWithNoExtension, filesWithRestrictedChars, files, totalSize } =
        getSortedFilesAndMetadata(acceptedFiles);

      const canUpload = await checkIfCanUpload({ files, totalSize });

      if (!canUpload) {
        return null;
      }

      if (filesWithNoExtension.length) {
        const shouldCancelUpload = await new Promise<boolean>((resolve) => {
          showMissingFileExtensionsModal({
            items: filesWithNoExtension,
            onCancel: () => {
              resolve(true);
            },
            onConfirm: () => {
              resolve(false);
            },
          });
        });

        if (shouldCancelUpload) {
          return null;
        }
      }

      if (filesWithRestrictedChars.length) {
        const shouldCancelUpload = await new Promise<boolean>((resolve) => {
          showRestrictedCharacterConfirmationModal({
            items: filesWithRestrictedChars,
            onCancel: () => {
              resolve(true);
            },
            onConfirm: () => {
              resolve(false);
            },
          });
        });

        if (shouldCancelUpload) {
          return null;
        }

        const sanitizedFiles = filesWithRestrictedChars.map((file) => sanitizeRestrictedPathCharsInFile(file));
        files.push(...sanitizedFiles);
      }

      return {
        files,
        emptyFiles,
        filesWithNoExtension,
        totalSize,
      };
    },
    [checkIfCanUpload, showMissingFileExtensionsModal, showRestrictedCharacterConfirmationModal],
  );

  return {
    validateUpload,
  };
};
